/** Hook para gestión de selección masiva y operaciones bulk sobre equipos. */
import { useState, useCallback } from 'react';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';

/**
 * Gestiona la selección masiva y operaciones bulk sobre equipos.
 *
 * @param {Object} options
 * @param {Array} options.displayedEquipos - Lista de equipos actualmente visibles
 * @param {Function} options.refetch - Función para refrescar la lista principal
 * @returns {Object} Estado y handlers de selección masiva
 * @returns {Set} returns.selectedEquipos - Set de IDs de equipos seleccionados
 * @returns {boolean} returns.bulkProcessing - true mientras se ejecuta una operación bulk
 * @returns {Function} returns.toggleSelect - Alterna selección de un equipo por ID
 * @returns {Function} returns.selectAll - Selecciona todos los equipos visibles
 * @returns {Function} returns.deselectAll - Limpia la selección
 * @returns {Function} returns.bulkChangeEstado - Cambia estado de equipos seleccionados
 * @returns {Function} returns.bulkDelete - Elimina equipos seleccionados
 */
function useEquiposBulkActions({ displayedEquipos, refetch }) {
  const { showToast } = useToast();

  const [selectedEquipos, setSelectedEquipos] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  /**
   * Alterna selección de un equipo por ID
   * @param {number} equipoId - ID del equipo
   */
  const toggleSelect = useCallback((equipoId) => {
    setSelectedEquipos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(equipoId)) {
        newSet.delete(equipoId);
      } else {
        newSet.add(equipoId);
      }
      return newSet;
    });
  }, []);

  /**
   * Selecciona todos los equipos visibles
   */
  const selectAll = useCallback(() => {
    setSelectedEquipos(new Set(displayedEquipos.map(e => e.id)));
  }, [displayedEquipos]);

  /**
   * Limpia la selección
   */
  const deselectAll = useCallback(() => {
    setSelectedEquipos(new Set());
  }, []);

  /**
   * Cambia el estado de los equipos seleccionados masivamente
   * @param {string} nuevoEstado - Nuevo estado a aplicar
   * @returns {Promise<void>}
   */
  const bulkChangeEstado = useCallback(async (nuevoEstado) => {
    if (selectedEquipos.size === 0) return;

    // VALIDAR: Solo operar sobre equipos visibles en displayedEquipos
    const visibleIds = new Set(displayedEquipos.map(e => e.id));
    const validSelection = Array.from(selectedEquipos).filter(id => visibleIds.has(id));

    if (validSelection.length === 0) {
      showToast('Los equipos seleccionados no están visibles con el filtro actual', 'warning');
      deselectAll();
      return;
    }

    // Confirmar si hay equipos seleccionados pero ocultos
    const hiddenCount = selectedEquipos.size - validSelection.length;
    if (hiddenCount > 0) {
      if (!window.confirm(`Hay ${hiddenCount} equipo(s) seleccionado(s) ocultos por filtros. Solo se actualizarán los ${validSelection.length} visibles. ¿Continuar?`)) {
        return;
      }
    }

    setBulkProcessing(true);
    try {
      const results = await Promise.allSettled(
        validSelection.map(id =>
          apiService.updateEquipo(id, { estado: nuevoEstado })
        )
      );

      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (failed === 0) {
        showToast(`${successful} equipos actualizados`, 'success');
      } else {
        showToast(`${successful} actualizados, ${failed} fallaron`, 'warning');
      }

      refetch();
      deselectAll();
    } catch (error) {
      showToast('Error en actualización masiva', 'error');
      deselectAll();
    } finally {
      setBulkProcessing(false);
    }
  }, [selectedEquipos, displayedEquipos, refetch, deselectAll, showToast]);

  /**
   * Elimina los equipos seleccionados masivamente
   * @returns {Promise<void>}
   */
  const bulkDelete = useCallback(async () => {
    if (selectedEquipos.size === 0) return;

    // VALIDAR: Solo operar sobre equipos visibles
    const visibleIds = new Set(displayedEquipos.map(e => e.id));
    const validSelection = Array.from(selectedEquipos).filter(id => visibleIds.has(id));

    if (validSelection.length === 0) {
      showToast('Los equipos seleccionados no están visibles', 'warning');
      deselectAll();
      return;
    }

    // Confirmar con información de equipos ocultos
    const hiddenCount = selectedEquipos.size - validSelection.length;
    const confirmMsg = hiddenCount > 0
      ? `¿Eliminar ${validSelection.length} equipo(s) visible(s)? (${hiddenCount} oculto(s) no se eliminarán)`
      : `¿Eliminar ${validSelection.length} equipo(s)?`;

    if (!window.confirm(confirmMsg)) return;

    setBulkProcessing(true);
    try {
      const results = await Promise.all(
        validSelection.map(id => apiService.deleteEquipo(id))
      );
      const failed = results.filter(r => !r.success);
      if (failed.length > 0) {
        showToast(`${failed.length} equipo(s) no pudieron eliminarse`, 'error');
      } else {
        showToast(`${validSelection.length} equipos eliminados`, 'success');
      }
      refetch();
      deselectAll();
    } catch (error) {
      showToast('Error en eliminación masiva', 'error');
      deselectAll();
    } finally {
      setBulkProcessing(false);
    }
  }, [selectedEquipos, displayedEquipos, refetch, deselectAll, showToast]);

  return {
    selectedEquipos,
    bulkProcessing,
    toggleSelect,
    selectAll,
    deselectAll,
    bulkChangeEstado,
    bulkDelete,
  };
}

export default useEquiposBulkActions;
